/**
 * One of the AI's durable questions (R1 Q2, technical design §10.4) as a row
 * of the document overview's "Questions for your team" list (draft page
 * redesign). The list keeps one row open at a time; this row only draws
 * itself and answers through the questions store (the question PATCH).
 *
 * Closed: a number (a green check once answered), the question on one line,
 * its state ("Answered", or the section the answer goes to) and a chevron.
 *
 * Open: the plain helper line (`plainQuestionReason`), any answer options
 * (a recommended one first, judgement and decision questions only), the
 * answer box and the actions:
 *  - "Save and update draft" (`update_draft`, the filled action) and
 *    "Save only" (`answer_only`), offered while the document's question list
 *    says drafting updates are on and the question affects a section; else a
 *    single "Save answer" (`answer_only`);
 *  - "Skip for now" (defer) while the question waits and has not been skipped.
 * An answered question keeps its answer editable (a new answer replaces the
 * old one); it has no Skip.
 */

import { useState } from 'react'
import { Check, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import type { QuestionAnswerEffect, QuestionAsk } from '@/lib/api'
import { answerOptions, continuationNotice, isAnsweredQuestion, isWaitingQuestion, plainQuestionReason } from '@/lib/question-view'
import {
  QUESTION_ANSWERED_STATE,
  QUESTION_ANSWER_LABEL,
  QUESTION_ANSWER_PLACEHOLDER,
  QUESTION_DEFERRED,
  QUESTION_RECOMMENDED,
  QUESTION_SAVE,
  QUESTION_SAVE_AND_UPDATE,
  QUESTION_SAVE_ONLY,
  QUESTION_SAVING,
  QUESTION_SKIP,
  QUESTION_SKIPPED_HEADING,
} from '@/lib/question-copy'
import { selectDocumentQuestions, useQuestionsStore } from '@/stores/questions-store'
import { useBidReadOnly } from '@/hooks/useViewOnly'
import { ViewOnlyNotice } from '@/components/ViewOnlyNotice'

interface QuestionBlockProps {
  bidId: string
  ask: QuestionAsk
  /** Its place in the list, from 1. */
  number: number
  isOpen: boolean
  onToggle: () => void
  /** The heading of the section the answer goes to, shown on a row still waiting. */
  sectionHeading?: string | null
  className?: string
}

export function QuestionBlock({ bidId, ask, number, isOpen, onToggle, sectionHeading = null, className }: QuestionBlockProps) {
  const isAnswered = isAnsweredQuestion(ask)
  const bodyId = `question-${ask.id}-body`

  return (
    <li className={className} data-testid="question-block" data-open={isOpen ? 'true' : 'false'}>
      <button
        type="button"
        className={cn('flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors', !isOpen && 'hover:bg-muted/40')}
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <span
          className={cn(
            'inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-xs tabular-nums',
            isAnswered
              ? 'bg-[hsl(var(--status-success)/0.14)] text-[hsl(var(--status-success))]'
              : 'bg-muted text-muted-foreground'
          )}
        >
          {isAnswered ? <Check className="h-3.5 w-3.5" aria-hidden /> : number}
        </span>
        <span className={cn('min-w-0 flex-1 text-sm text-foreground', isOpen ? 'font-medium' : 'truncate')}>{ask.text}</span>
        {isAnswered ? (
          <span className="shrink-0 text-xs text-[hsl(var(--status-success))]">{QUESTION_ANSWERED_STATE}</span>
        ) : (
          sectionHeading && (
            <span className="hidden max-w-[40%] shrink-0 truncate text-xs text-muted-foreground sm:inline">{sectionHeading}</span>
          )
        )}
        <ChevronRight
          className={cn('h-3.5 w-3.5 shrink-0 text-subtle transition-transform', isOpen && 'rotate-90')}
          aria-hidden
        />
      </button>
      {isOpen && <QuestionBody id={bodyId} bidId={bidId} ask={ask} />}
    </li>
  )
}

// ===================
// Open row
// ===================

function QuestionBody({ id, bidId, ask }: { id: string; bidId: string; ask: QuestionAsk }) {
  const action = useQuestionsStore((s) => s.actions[ask.id])
  const answerQuestion = useQuestionsStore((s) => s.answer)
  const deferQuestion = useQuestionsStore((s) => s.defer)
  // The server's word, from the document's question list: off while the whole-document job is switched off (R1 J8′).
  const canUpdateDraft = useQuestionsStore((s) => (ask.artifactId ? selectDocumentQuestions(s, ask.artifactId).canUpdateDraft : false))
  const [answer, setAnswer] = useState(ask.answer ?? '')

  const isWaiting = isWaitingQuestion(ask)
  const isDeferred = ask.question.status === 'deferred'
  const { recommended, others } = isWaiting ? answerOptions(ask.question) : { recommended: null, others: [] }
  const helper = plainQuestionReason(ask.question.reason)
  const isSaving = action?.kind === 'saving'
  const canSave = answer.trim().length > 0 && !isSaving
  const offersUpdate = canUpdateDraft && ask.question.targets.length > 0
  const notice = action?.kind === 'answered' && action.continuation ? continuationNotice(action.continuation) : null

  const savingLabel = (effect: QuestionAnswerEffect, label: string): string =>
    action?.kind === 'saving' && action.action === effect ? QUESTION_SAVING : label
  const handleSave = (effect: QuestionAnswerEffect) => void answerQuestion(bidId, ask.id, answer, effect)
  // A read-only user (an org viewer, or a member who is not on the bid) reads
  // the question and any answer; answering is refused them.
  const { isReadOnly } = useBidReadOnly(bidId)

  if (isReadOnly) {
    return (
      <div id={id} className="flex flex-col gap-2 px-3 pb-3 sm:pl-11">
        {helper && <p className="text-meta">{helper}</p>}
        {isDeferred && <p className="text-meta">{QUESTION_DEFERRED}</p>}
        {ask.answer && <p className="whitespace-pre-wrap text-sm text-foreground">{ask.answer}</p>}
        <ViewOnlyNotice />
      </div>
    )
  }

  const optionButton = (label: string, variant: 'outline' | 'ghost') => (
    <Button
      key={label}
      type="button"
      variant={variant}
      size="sm"
      aria-pressed={answer === label}
      disabled={isSaving}
      className={cn('h-auto min-h-7 justify-start whitespace-normal px-2 py-1 text-left text-xs', answer === label && 'bg-muted')}
      onClick={() => setAnswer(label)}
    >
      {label}
    </Button>
  )

  return (
    <div id={id} className="flex flex-col gap-2 px-3 pb-3 sm:pl-11">
      {helper && <p className="text-meta">{helper}</p>}
      {isDeferred && <p className="text-meta">{QUESTION_DEFERRED}</p>}

      {(recommended || others.length > 0) && (
        <div role="group" aria-label="Answer options" className="flex flex-col items-start gap-1">
          {recommended && optionButton(recommended.label, 'outline')}
          {recommended?.reason && <p className="text-meta pb-1">{`${QUESTION_RECOMMENDED} · ${recommended.reason}`}</p>}
          {others.map((option) => optionButton(option.label, 'ghost'))}
        </div>
      )}

      <Textarea
        aria-label={QUESTION_ANSWER_LABEL}
        placeholder={QUESTION_ANSWER_PLACEHOLDER}
        value={answer}
        rows={2}
        disabled={isSaving}
        className="min-h-[64px] bg-muted text-sm"
        onChange={(event) => setAnswer(event.target.value)}
      />
      <div className="flex flex-wrap items-center gap-2">
        {offersUpdate ? (
          <>
            <Button type="button" size="sm" disabled={!canSave} onClick={() => handleSave('update_draft')}>
              {savingLabel('update_draft', QUESTION_SAVE_AND_UPDATE)}
            </Button>
            <Button type="button" variant="ghost" size="sm" disabled={!canSave} onClick={() => handleSave('answer_only')}>
              {savingLabel('answer_only', QUESTION_SAVE_ONLY)}
            </Button>
          </>
        ) : (
          <Button type="button" size="sm" disabled={!canSave} onClick={() => handleSave('answer_only')}>
            {savingLabel('answer_only', QUESTION_SAVE)}
          </Button>
        )}
        {isWaiting && !isDeferred && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="px-2 text-muted-foreground hover:text-foreground"
            disabled={isSaving}
            onClick={() => void deferQuestion(bidId, ask.id)}
          >
            {QUESTION_SKIP}
          </Button>
        )}
      </div>
      {action?.kind === 'error' && <p role="alert" className="text-xs text-destructive">{action.message}</p>}
      {notice && (
        <div>
          <p className="text-meta">{notice.line}</p>
          {notice.skipped.length > 0 && (
            <>
              <p className="text-meta">{QUESTION_SKIPPED_HEADING}</p>
              <ul className="pl-3">
                {notice.skipped.map((line) => (
                  <li key={line} className="text-meta">{line}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  )
}
